import React, { useState } from 'react';
import { PDFDocument, PDFImage, rgb, StandardFonts } from 'pdf-lib';
import { Button } from '@mui/material';

/** Text overlay params */
export interface TextItem {
  text: string;
  x: number;
  y: number;
  size?: number;
}

/** Image overlay params */
export interface ImageItem {
  /** Raw bytes of a PNG or JPG */
  bytes: ArrayBuffer;
  /** 'png' or 'jpg' */
  format: 'png' | 'jpg';
  x: number;
  y: number;
  width?: number | undefined;
  height?: number | undefined;
  scale?: number;
}

/** Named-params for PDF generation */
export interface GenerateOptions {
  /** Blank PDF template bytes */
  existingPdfBytes: ArrayBuffer;
  /** Array of text overlays */
  texts: TextItem[];
  /** Optional array of image overlays */
  images?: ImageItem[];
}

/**
 * Loads an existing PDF, overlays texts & images, and returns a Blob.
 */
export async function generatePdfWithContent({ existingPdfBytes, texts, images = [] }: GenerateOptions): Promise<Blob> {
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const [firstPage] = pdfDoc.getPages();

  // Embed all images in parallel
  const embeddedImages = await Promise.all(
    images.map((img) => (img.format === 'png' ? pdfDoc.embedPng(img.bytes) : pdfDoc.embedJpg(img.bytes)))
  );

  // Draw images with scale support
  embeddedImages.forEach((embeddedImage, idx) => {
    const { x, y, width, height, scale } = images[idx];

    let drawWidth: number;
    let drawHeight: number;

    if (scale != null) {
      const dims = embeddedImage.scale(scale);
      drawWidth = dims.width;
      drawHeight = dims.height;
    } else if (width != null && height != null) {
      drawWidth = width;
      drawHeight = height;
    } else if (width != null) {
      const ratio = width / embeddedImage.width;
      drawWidth = width;
      drawHeight = embeddedImage.height * ratio;
    } else if (height != null) {
      const ratio = height / embeddedImage.height;
      drawWidth = embeddedImage.width * ratio;
      drawHeight = height;
    } else {
      drawWidth = embeddedImage.width;
      drawHeight = embeddedImage.height;
    }

    firstPage.drawImage(embeddedImage, {
      x,
      y,
      width: drawWidth,
      height: drawHeight,
    });
  });

  // Draw texts
  texts.forEach(({ text, x, y, size = 12 }) => {
    firstPage.drawText(text, {
      x,
      y,
      size,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
}
